import React from "react"

import MuiAppBar from "@mui/material/AppBar"
import Box from "@mui/material/Box"
import Skeleton from "@mui/material/Skeleton"
import Toolbar from "@mui/material/Toolbar"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import Stack from "@mui/system/Stack"
import { useTranslation } from "react-i18next"

import useSession from "@hooks/useSession"

import FavoriteFeature from "@components/FavoriteFeature"
import { IconWrapper } from "@components/IconWrapper"

import { FeatureNavItem } from "@constants/features"

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  boxShadow: "none",
  color: theme.palette.text.primary,
}))

interface AppbarProps {
  action?: React.ReactNode
  favoriteFeature?: string
  feature?: FeatureNavItem
}

export const appBarHeight = 24

const Appbar: React.FC<AppbarProps> = ({
  action,
  favoriteFeature,
  feature,
}: AppbarProps) => {
  const { t } = useTranslation()
  const { hasPermission } = useSession()

  if (feature?.permission && !hasPermission(feature.permission)) {
    return null
  }

  return (
    <AppBar
      sx={{
        // has to match color of background Stack in UnprotectedLayout
        backgroundColor: "transparent",
        border: 0,
        outline: "none",
        left: "unset",
        top: "unset",
        p: 0,
        position: "relative",
        width: "100%",
        zIndex: "unset",
      }}
      elevation={0}
    >
      <Toolbar
        variant="dense"
        sx={{
          minHeight: "auto",
          "@media (min-width: 600px)": {
            px: 0,
          },
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <IconWrapper size={20} sx={{ color: "inherit" }}>
              {feature?.icon ?? <Skeleton width={20} height={20} />}
            </IconWrapper>

            <Typography variant="h4">
              {feature ? (
                t(feature.translatedTextKey)
              ) : (
                <Skeleton width={200} />
              )}
            </Typography>
          </Stack>

          <Stack direction={"row"} gap={1} alignItems={"center"}>
            {favoriteFeature && <FavoriteFeature feature={favoriteFeature} />}
            {action && <Box>{action}</Box>}
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  )
}

export default Appbar
